import { createSignal } from 'solid-js'

import { translate } from '../i18n'

export type FastRange = {
   start: number
   end: number
}

export type HistoryTimeBlocksDetail = {
   periodKey: string
   periodLabel: string
   ranges: FastRange[]
   currentFastRange?: FastRange | null
   emittedAt: number
}

/**
 * Filters and normalizes an array of fast ranges, dropping invalid ones.
 *
 * @param ranges - The candidate ranges.
 * @returns The valid ranges.
 */
export function normalizeRanges(ranges: FastRange[]): FastRange[] {
   const result: FastRange[] = []
   for (let i = 0; i < ranges.length; i += 1) {
      const range = ranges[i]
      if (!range) {
         continue
      }
      const { start, end } = range
      if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) {
         continue
      }
      result.push({ start, end })
   }

   return result
}

/**
 * Normalizes a single fast range, or null when invalid.
 *
 * @param range - The candidate range.
 * @returns The valid range, or null.
 */
export function normalizeRange(range?: FastRange | null): FastRange | null {
   if (!range) {
      return null
   }

   const { start, end } = range
   if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) {
      return null
   }

   return { start, end }
}

/**
 * Builds the default history time-blocks detail (past month, empty).
 *
 * @returns The default detail.
 */
function createDefaultDetail(): HistoryTimeBlocksDetail {
   return {
      periodKey: 'month',
      periodLabel: translate('Past Month'),
      ranges: [],
      currentFastRange: null,
      emittedAt: Date.now(),
   }
}

const [historyTimeBlocksDetail, setHistoryTimeBlocksDetail] =
   createSignal<HistoryTimeBlocksDetail>(createDefaultDetail())

/**
 * Applies a history time-blocks update to the store.
 *
 * @param detail - The event detail, if any.
 */
export function applyHistoryTimeBlocksUpdate(detail?: HistoryTimeBlocksDetail | null) {
   if (!detail || typeof detail !== 'object') {
      return
   }
   const normalizedCurrentFastRange = normalizeRange(detail.currentFastRange)
   setHistoryTimeBlocksDetail({
      periodKey: typeof detail.periodKey === 'string' ? detail.periodKey : 'month',
      periodLabel:
         typeof detail.periodLabel === 'string' ?
            detail.periodLabel
         :  translate('Past Month'),
      ranges: Array.isArray(detail.ranges) ? normalizeRanges(detail.ranges) : [],
      currentFastRange: normalizedCurrentFastRange,
      emittedAt:
         typeof detail.emittedAt === 'number' && Number.isFinite(detail.emittedAt) ?
            detail.emittedAt
         :  Date.now(),
   })
}

export { historyTimeBlocksDetail }
